import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { portalAction } from 'root/redux/action/portal.action';
import { useTranslation } from 'react-i18next';

const DATERANGES = [
    'transaction:transaction.history.filter.today',
    'transaction:transaction.history.filter.3days',
    'transaction:transaction.history.filter.aweek',
    'transaction:transaction.history.filter.amonth',
];

const SELECT_ENUM = {
    provider: {
        title: 'provider',
    },
    outstandingType: {
        title: 'outstandingType',
    },
};

const OUTSTANDING_LIST = [
    {
        id: 0,
        name: 'Unsettled Bet',
        value: 'OT',
        tabHeader: 'transaction:transaction.sportsOutstanding.unsettledBet',
        selected: false,
        fallback: 'Unsettled Bet',
    },
    {
        id: 1,
        name: 'Settled Bet',
        value: 'ST',
        tabHeader: 'transaction:transaction.sportsOutstanding.settledBet',
        selected: false,
        fallback: 'Settled Bet',
    },
];
const COLUMNS = [
    {
        key: 'date',
        render: (data) => (data?.ticketDate ? moment(data.ticketDate).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
        key: 'matchDate',
        render: (data) => (data?.matchDate ? moment(data.matchDate).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
        key: 'ticketId',
        render: (data) => data?.ticketId ?  formatMaxChars(data.ticketId): '-',
    },
    {
        key: 'betType',
        render: (data) => (data?.betType ? formatMaxChars(data.betType) : '-'),
    },
    {
        key: 'betChoice',
        render: (data) => (data?.betChoice ? data.betChoice : '-'),
    },
    {
        key: 'sportType',
        render: (data) => (data?.sportType ? formatMaxChars(data.sportType) : '-'),
    },
    {
        key: 'odds',
        render: (data) => data?.odds || '-',
    },
    {
        key: 'home',
        render: (data) => (data?.homeTeam ? formatMaxChars(data.homeTeam) : '-'),
    },
    {
        key: 'away',
        render: (data) => (data?.awayTeam ? formatMaxChars(data.awayTeam) : '-'),
    },
    {
        key: 'betAmount',
        render: (data) => data?.betAmount || '-',
    },
    {
        key: 'status',
        render: (data, t) => data?.status || '-',
    },
];

const formatMaxChars = (value) => {
    return value.length >= 10 ? value.slice(0, 10).concat('...') : value;
};

const getInitialRangeDate = () => {
    const timeRange = 6 * 24 * 3600000;
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - timeRange);

    return { startDate, endDate };
};

const useSportsOutstanding = () => {
    const [sportsOutstanding, setSportsOutstanding] = useState([]);
    const [sorting, setSorting] = useState({ createdDate: 'desc' });
    const [rangeDate, setRangeDate] = useState(getInitialRangeDate());
    const [selectedDateRangeDisplay, setSelectedDateRangeDisplay] = useState(DATERANGES);
    const [selectedDateRange, setSelectedDateRange] = useState(2);
    const [selectedOutstandingIndex, setSelectedOutstandingIndex] = useState(0);
    const [providerList, setProviderList] = useState([]);
    const [selectedProviderIndex, setSelectedProviderIndex] = useState(0);
    const [paging, setPaging] = useState({ currentPage: 1, itemPerPage: 5, totalCount: 0, totalPage: 0 });
    const language = useSelector((s) => s.language);
    const dispatch = useDispatch();

    const { currentPage, itemPerPage, totalPage, totalCount } = paging;
    const { startDate, endDate } = rangeDate;

    const { t } = useTranslation(['history', 'settings', 'transaction', 'providerName']);

    useEffect(() => {
        getProviderList();
        getSportsOutstanding();
    }, [sorting, currentPage]);

    let datepickerLanguage = '';
    if (language.countryLanguageKey === 'TH_TH') {
        datepickerLanguage = 'th';
    } else if (language.countryLanguageKey === 'ZH_MY') {
        datepickerLanguage = 'zhCN';
    }

    const getProviderList = async () => {
        try {
            const { country } = language;
            const data = await window.SPL_Member.getBetHistoryProviders();
            if (!data) return;
            const list = data.reduce((acc, curr, i) => {
                acc.push({
                    id: i,
                    name: t(`providerName:ProviderName.${curr}`),
                    value: curr,
                    tabHeader: t(`providerName:ProviderName.${curr}`),
                    selected: false,
                });
                return acc;
            }, []);

            let countryList = [];
            switch (country) {
                case 'MY':
                    countryList = list.filter((val) => val.value !== 'M8');
                    break;
                case 'SG':
                    countryList = list.filter((val) => val.value !== 'IBC' && val.value !== 'IBC3');
                    break;
                default:
                    countryList = list;
                    break;
            }

            setProviderList(countryList);
        } catch (err) {
            //more error handling?
        }
    };

    const getSportsOutstanding = async () => {
        dispatch(portalAction.portalIsLoading(true));

        const sort = Object.entries(sorting)
            .map(([k, v]) => `${k},${v.toUpperCase()}`)
            .join(';');

        const params = {
            provider: providerList[selectedProviderIndex]?.value || null,
            bhType: OUTSTANDING_LIST[selectedOutstandingIndex].value,
            start: new Date(new Date(startDate).setHours(0, 0, 0)),
            end: new Date(new Date(endDate).setHours(23, 59, 59)),
            page: currentPage - 1,
            size: itemPerPage,
            sort,
        };

        window.SPL_Member.getBetHistories(params)
            .then((data) => {

                setSportsOutstanding(data.content);

                let totalCount = data.totalElements;
                let totalPage = data.totalPages;
                setPaging({
                    ...paging,
                    totalCount: totalCount,
                    totalPage: totalPage,
                });
                dispatch(portalAction.portalIsLoading(false));
            })
            .catch((err) => {})
            .finally(() => dispatch(portalAction.portalIsLoading(false)));
    };

    const handleSelectFieldChanged = (e, type, isMobile) => {
        const value = !isMobile ? e.target.value : e;
        switch (type) {
            case SELECT_ENUM.provider.title:
                setSelectedProviderIndex(value);
                break;
            case SELECT_ENUM.outstandingType.title:
                setSelectedDateRangeDisplay(Number(value) === 1 ? DATERANGES.slice(0, 3) : DATERANGES);
                const selected = OUTSTANDING_LIST.findIndex((ot, index) => ot.id === Number(value));
                setSelectedOutstandingIndex(selected);

                break;
            default:
                return;
        }
    };

    const handleDateChanged = (date, isStartDate) => {
        const newRangeDate = { ...rangeDate };
        if (isStartDate) {
            newRangeDate.startDate = date;
        } else {
            newRangeDate.endDate = date;
        }
        setRangeDate(newRangeDate);
    };

    const handlePrevNextClick = (isPrev, firstLast) => {
        if (firstLast) {
            if (firstLast === 'first') {
                handlePageClick(1);
            } else {
                handlePageClick(totalPage);
            }
        } else {
            if (isPrev) {
                handlePageClick(currentPage - 1);
            } else {
                handlePageClick(currentPage + 1);
            }
        }
    };

    const handlePageClick = (newPageNum) => {
        if (!(currentPage === newPageNum || newPageNum === 0 || newPageNum > totalPage)) {
            setPaging({ ...paging, currentPage: newPageNum });
        }
    };

    const handleDateRange = (type) => {
        const newRangeDate = { ...rangeDate, endDate: new Date() };

        switch (type) {
            case 0:
                newRangeDate.startDate = new Date(newRangeDate.endDate.getTime());
                break;
            case 1:
                newRangeDate.startDate = new Date(newRangeDate.endDate.getTime() - 2 * 24 * 3600000);
                break;
            case 2:
                newRangeDate.startDate = new Date(newRangeDate.endDate.getTime() - 6 * 24 * 3600000);
                break;
            case 3:
                newRangeDate.startDate = new Date(newRangeDate.endDate.getTime() - 30 * 24 * 3600000);
                break;
            default:
                break;
        }

        setSelectedDateRange(type);
        setRangeDate(newRangeDate);
    };

    const sortSportsOutstanding = (key) => {
        const newSorting = {};
        newSorting[key] = sorting[key] === 'desc' ? 'asc' : 'desc';
        setSorting(newSorting);
    };

    const pagingList = useMemo(() => {
        const { currentPage, totalPage } = paging;
        const list = [];

        for (let i = 0; i < totalPage; i++) {
            list.push({ pageNum: i + 1, active: currentPage === i });
        }

        return list;
    }, [currentPage, totalPage]);

    return {
        OUTSTANDING_LIST,
        COLUMNS,
        DATERANGES,
        SELECT_ENUM,
        datepickerLanguage,
        selectedDateRange,
        currentPage,
        itemPerPage,
        totalPage,
        totalCount,
        pagingList,
        sportsOutstanding,
        startDate,
        endDate,
        sorting,
        providerList,
        selectedOutstandingIndex,
        selectedProviderIndex,
        selectedDateRangeDisplay,
        handleSelectFieldChanged,
        getSportsOutstanding,
        handleDateRange,
        handleDateChanged,
        handlePageClick,
        handlePrevNextClick,
        sortSportsOutstanding,
    };
};

export default useSportsOutstanding;
